// Rótulos de los hitos del hilo que NO son transiciones del grafo de la
// receta — fuente única para el dashboard (DetalleObservacionPostventa) y
// la PWA (hilo de la observación en cartera).
//
// Dos familias (ADR-0134 §7–§8):
//
// 1. El hito sintético de la migración de facility: el script
//    backfill-facility-delegation-to-observation escribe UN evento por
//    hallazgo con `event_type = OBSERVATION_MIGRATED_EVENT_TYPE`. Ese tipo
//    es la marca de máquina con que la reversión lo encuentra (nunca la
//    nota, que es texto visible). Lleva `to_status` = el estado del hilo
//    en que quedó, así que si un cliente lo pasara por la rama de fase
//    leería «Asignó…», «Anuló…» o «Marcó como resuelta»: no fue nadie,
//    fue la migración.
//
// 2. El historial de la delegación retirada (ADR-0074): `in_progress`,
//    `reported_resolved`, `resolved`, `resolved_direct`, `cancelled`,
//    `reopened` (core-engine `delegationEventType`), y `assigned` /
//    `released` de `/delegate`, que llevan claves de la DELEGACIÓN
//    (`pendiente`, `en_curso`, …) en from/to. Esas claves no existen en
//    el grafo del hilo: pasadas por `observationPhase` caían a «Cambió el
//    estado». `assigned` / `reassigned` también los escribe el hilo
//    (/assign), con claves del grafo; sólo el `to_status` distingue uno
//    de otro, y por eso se compara contra `DELEGATION_STATUSES`.
//
// 3. El cierre por falta de respuesta (ADR-0142): el job programado de
//    core-api escribe `OBSERVATION_AUTO_CLOSED_EVENT_TYPE` como fila de
//    SISTEMA (actor NULL, carril NULL) — no es un veredicto del
//    propietario y jamás se rotula como conformidad —, y la reapertura
//    del propietario dentro de la ventana escribe
//    `OBSERVATION_REOPENED_EVENT_TYPE` (actor = el propietario). Ninguno
//    de los dos mueve el hilo por el grafo de la forma que su `toStatus`
//    sugiere: el cierre no cambia de nodo (`resuelta → resuelta`) y la
//    reapertura no es un «No conforme».
//
// 4. La visita (F8, fase 2 corte 6): `visit_scheduled` / `visit_rescheduled`
//    los escribe el jefe al agendar o cambiar la fecha, y `visit_requested`
//    el contratista al pedir una nueva visita (ADR-0123). Los tres van de
//    `asignada` a `asignada`: por la rama de fase leerían «Asignó…».
//
// Los clientes resuelven esto ANTES de su rama por fase de `toStatus`.
import { DELEGATION_STATUSES, VISIT_RESCHEDULED_EVENT_TYPE, VISIT_SCHEDULED_EVENT_TYPE, VISIT_AUTO_CONFIRMED_EVENT_TYPE, VISIT_CONFIRMED_EVENT_TYPE, VISIT_OTHER_TIME_REQUESTED_EVENT_TYPE, } from "@bitacorapro/core-engine";
/** `finding_events.event_type` del hito sintético de la migración
 *  delegación → hilo (ADR-0134 §7). Marca de máquina: la reversión del
 *  script lo identifica por este tipo, nunca por la nota. */
export const OBSERVATION_MIGRATED_EVENT_TYPE = "observation_migrated";
/** La nota visible que el script deja en el hito sintético. */
export const OBSERVATION_MIGRATED_NOTE = "Migrado desde el seguimiento anterior";
/** El rótulo del hito sintético en el hilo. */
export const OBSERVATION_MIGRATED_LABEL = "Migrado al nuevo seguimiento";
/** `finding_events.event_type` del cierre por falta de respuesta
 *  (ADR-0142). Fila de sistema; su `note` guarda la explicación (días,
 *  ancla, fechas de los recordatorios). */
export const OBSERVATION_AUTO_CLOSED_EVENT_TYPE = "observation_auto_closed";
/** `finding_events.event_type` de la reapertura de un cierre por falta de
 *  respuesta (ADR-0142 §4): un toque del propietario dentro de la
 *  ventana. No cuenta como apelación ni como «No conforme». */
export const OBSERVATION_REOPENED_EVENT_TYPE = "observation_reopened";
/** Rótulo del cierre por falta de respuesta en el hilo. */
export const OBSERVATION_AUTO_CLOSED_LABEL = "Cerrada por falta de respuesta";
/** Rótulo de la reapertura en el hilo. */
export const OBSERVATION_REOPENED_LABEL = "Reabrió la observación";
/** `finding_events.event_type` del pedido de nueva visita del contratista
 *  (ADR-0123). Su `note` es el códec `motivo · detalle`
 *  (`decodeVisitRequestNote`). */
export const VISIT_REQUESTED_EVENT_TYPE = "visit_requested";
/** Rótulos de los hitos de la visita (F8). */
export const VISIT_SCHEDULED_LABEL = "Agendó la visita";
export const VISIT_RESCHEDULED_LABEL = "Se reagendó la visita";
export const VISIT_REQUESTED_LABEL = "Pidió una nueva visita";
/** ADR-0161 — la respuesta del propietario a la visita propuesta. */
export const VISIT_CONFIRMED_LABEL = "Confirmó la visita";
export const VISIT_OTHER_TIME_REQUESTED_LABEL = "Pidió otro horario para la visita";
export const VISIT_AUTO_CONFIRMED_LABEL = "Visita confirmada sin respuesta del propietario";
/** Eventos de la delegación retirada (ADR-0074) → su rótulo en el hilo. */
export const LEGACY_DELEGATION_EVENT_LABEL = {
    in_progress: "Delegación: en curso",
    reported_resolved: "El contratista reportó resuelta",
    resolved: "Resuelta",
    resolved_direct: "Cerrada sin reporte del contratista",
    cancelled: "Delegación anulada",
    reopened: "Delegación reabierta",
};
/** `assigned` / `reassigned` de `/delegate` (to_status = clave de la
 *  delegación). */
export const LEGACY_DELEGATION_ASSIGNED_LABEL = "Asignada al contratista (seguimiento anterior)";
/** `released` de `/delegate`: el contratista que salía en una reasignación. */
export const LEGACY_DELEGATION_RELEASED_LABEL = "Contratista liberado (seguimiento anterior)";
const DELEGATION_KEYS = new Set(DELEGATION_STATUSES);
function isDelegationKey(status) {
    return status != null && DELEGATION_KEYS.has(status);
}
export function nonGraphTimelineLabel(ev) {
    if (ev.eventType === OBSERVATION_MIGRATED_EVENT_TYPE) {
        return { kind: "migrated", label: OBSERVATION_MIGRATED_LABEL };
    }
    if (ev.eventType === OBSERVATION_AUTO_CLOSED_EVENT_TYPE) {
        return { kind: "auto_closed", label: OBSERVATION_AUTO_CLOSED_LABEL };
    }
    if (ev.eventType === OBSERVATION_REOPENED_EVENT_TYPE) {
        return { kind: "reopened", label: OBSERVATION_REOPENED_LABEL };
    }
    if (ev.eventType === VISIT_SCHEDULED_EVENT_TYPE) {
        return { kind: "visit_scheduled", label: VISIT_SCHEDULED_LABEL };
    }
    if (ev.eventType === VISIT_RESCHEDULED_EVENT_TYPE) {
        return { kind: "visit_rescheduled", label: VISIT_RESCHEDULED_LABEL };
    }
    if (ev.eventType === VISIT_REQUESTED_EVENT_TYPE) {
        return { kind: "visit_requested", label: VISIT_REQUESTED_LABEL };
    }
    // ADR-0161: from = to = el estado (no son transiciones del grafo).
    if (ev.eventType === VISIT_CONFIRMED_EVENT_TYPE) {
        return { kind: "visit_confirmed", label: VISIT_CONFIRMED_LABEL };
    }
    if (ev.eventType === VISIT_OTHER_TIME_REQUESTED_EVENT_TYPE) {
        return { kind: "visit_other_time_requested", label: VISIT_OTHER_TIME_REQUESTED_LABEL };
    }
    if (ev.eventType === VISIT_AUTO_CONFIRMED_EVENT_TYPE) {
        return { kind: "visit_auto_confirmed", label: VISIT_AUTO_CONFIRMED_LABEL };
    }
    if (Object.hasOwn(LEGACY_DELEGATION_EVENT_LABEL, ev.eventType)) {
        return { kind: "legacy_delegation", label: LEGACY_DELEGATION_EVENT_LABEL[ev.eventType] };
    }
    if ((ev.eventType === "assigned" || ev.eventType === "reassigned") &&
        isDelegationKey(ev.toStatus)) {
        return { kind: "legacy_delegation", label: LEGACY_DELEGATION_ASSIGNED_LABEL };
    }
    // `released` sólo lo escribe `/delegate` (el hilo no libera: reasigna).
    if (ev.eventType === "released") {
        return { kind: "legacy_delegation", label: LEGACY_DELEGATION_RELEASED_LABEL };
    }
    return null;
}
//# sourceMappingURL=timeline-labels.js.map